import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Navigation } from "../cmps/homepage/navigation";
import { FooterSection } from "../cmps/homepage/footer-section";
import { Button } from "../components/ui/button";
import { ArrowRight } from "lucide-react";

const steps = [
  {
    number: "01",
    title: "Tell us about your project",
    description: "Share scope, timeline, contractors and budget."
  },
  {
    number: "02",
    title: "We assign a supervisor",
    description: "A vetted domain expert is matched within 48 hours."
  },
  {
    number: "03",
    title: "Track progress live",
    description: "Milestones, reports, files and risk flags in one place."
  },
  {
    number: "04",
    title: "Approve and pay confidently",
    description: "Release funds only after verified delivery."
  },
];

const deliverables = [
  "Dedicated supervisor matched to your domain",
  "Live dashboard with milestones, files and risk flags",
  "Weekly written reports with photographic evidence",
  "Independent milestone sign-off before payments",
  "On-call escalation for high-severity risks",
  "Closeout report and lessons learned"
];

export default function HowItWorksPage() {
  useEffect(() => {
    document.title = "How It Works | MANAGE ME";
  }, []);

  return (
    <div className="homepage-root">
      <main className="relative min-h-screen overflow-x-hidden">
        <Navigation />

        <section className="relative py-24 md:py-32 px-4">
          <div className="max-w-4xl mx-auto">
            <div className="mb-8">
              <span className="inline-flex items-center gap-3 text-sm font-mono text-foreground/60 mb-8">
                <span className="w-8 h-px bg-foreground/30" />
                Process
              </span>
            </div>

            <h1 className="text-6xl md:text-7xl lg:text-8xl font-display tracking-tight leading-[0.9] mb-8">
              Four steps from
              <br />
              <span className="text-foreground/40">kickoff to closeout.</span>
            </h1>
          </div>
        </section>

        <section className="relative py-24 px-4 border-t border-foreground/10">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-16">
              {steps.map((step, index) => (
                <div key={step.number} className="group">
                  <div className="mb-8">
                    <span className="text-6xl md:text-7xl font-display text-foreground/10 group-hover:text-foreground/20 transition-colors">
                      {step.number}
                    </span>
                  </div>
                  <h3 className="text-2xl md:text-3xl font-display mb-4 leading-tight">{step.title}</h3>
                  <p className="text-lg text-foreground/60 leading-relaxed">{step.description}</p>
                  {index < steps.length - 1 && (
                    <div className="mt-12 h-24 border-l-2 border-foreground/10 md:hidden" />
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="relative py-24 px-4 bg-foreground/5">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-display mb-8">What you get</h2>
            <p className="text-xl text-foreground/70 mb-16">Evidence at every step.</p>

            <div className="grid sm:grid-cols-2 gap-6 mb-12">
              {deliverables.map((deliverable, index) => (
                <div key={index} className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="flex items-center justify-center h-6 w-6 rounded-full bg-foreground text-background text-sm font-bold">
                      ✓
                    </div>
                  </div>
                  <p className="text-foreground/70 leading-relaxed">{deliverable}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="relative py-24 px-4 border-t border-foreground/10">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-display mb-8">Ready to get started?</h2>
            <p className="text-xl text-foreground/60 mb-12">
              Tell us about your project and we'll match you with the right supervisor.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/auth/signup">
                <Button
                  size="lg"
                  className="bg-foreground hover:bg-foreground/90 text-background px-8 h-14 text-base rounded-full group"
                >
                  Start a project
                  <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button
                  size="lg"
                  variant="outline"
                  className="h-14 px-8 text-base rounded-full border-foreground/20 hover:bg-foreground/5"
                >
                  Talk to our team
                </Button>
              </Link>
            </div>
          </div>
        </section>

        <FooterSection />
      </main>
    </div>
  );
}
